import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import CONSTANTS from '../../_constants/constants';
import { environment } from '../../../environments/environment';
import { DefaultParams } from '../../_shared/interfaces/defaultParams';

import { SessionService } from '../../_shared/services/session.service';
import { UtilService } from '../../_shared/services/util.service';

import { Currency } from './currency.model';
import { CompanyService } from '../../companies/shared/company.service';

interface ExtendedDefaultParams extends DefaultParams {
  by_name?: any;
  by_code?: any;
  by_decimal_places?: any;
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private utilService: UtilService,
    private companyService: CompanyService
  ) {}

  private apiUrl = environment.apiEndpoint + 'currencies';

  getFilterCriteria() {
    const defaultParams: ExtendedDefaultParams = {
      page: 1,
      sortDir: 'asc',
      sortedBy: 'name',
    };

    return defaultParams;
  }

  getCurrencies(params = null, companyId = null): Observable<any> {
    if (!params) {
      params = this.getFilterCriteria();
    }

    params.company_id = companyId || this.sessionService.getSelectedAccountCompany().id;

    let queryParams = new HttpParams({ fromObject: params });

    return this.http.get(this.apiUrl, { params: queryParams }).pipe(
      map((resp) => {
        if (resp['data']) {
          return {
            data: resp['data'],
            meta: this.utilService.getItemsMetaData(resp, this.getFilterCriteria()),
          };
        } else {
          return resp;
        }
      })
    );
  }

  getCurrency(id: string, params = null): Observable<Currency> {
    let queryParams = new HttpParams({ fromObject: params });

    return this.http.get<Currency>(`${this.apiUrl}/${id}`, {
      params: queryParams,
    });
  }

  // Save / Update
  saveCurrency(currencyId: string, currency: Currency, lang = ''): Observable<Currency> {
    const body = {
      currency: currency,
      lang: lang || CONSTANTS.DEFAULT_CONTENT_LANGUAGE,
    };

    if (!currencyId) {
      body['company_id'] = this.sessionService.getSelectedAccountCompany().id;

      return this.http.post<Currency>(`${this.apiUrl}`, body);
    } else {
      return this.http.put<Currency>(`${this.apiUrl}/${currencyId}`, body);
    }
  }

  changeCurrencyStatus(currency): Observable<Currency> {
    let newStatus = currency.status === CONSTANTS.ACTIVE ? CONSTANTS.INACTIVE : CONSTANTS.ACTIVE;

    const body = {
      set_status: true,
      check_if_used: true,
      status: newStatus,
      company_id: this.sessionService.getSelectedAccountCompany().id,
    };

    return this.http.put<Currency>(`${this.apiUrl}/${currency.id}`, body);
  }

  setAutomaticExchangeRate(currency: Currency, companyTimeZoneUtc): Observable<Currency> {
    const url = `${this.apiUrl}/${currency.id}/set_automatic_exchange_rate`;

    // const companyTimeZoneUtc = this.sessionService.getSelectedAccountCompany().time_zone.utc;

    return this.http.put<Currency>(url, {
      currency: currency,
      skip_check_role: true,
      time_zone_utc: companyTimeZoneUtc,
    });
  }
}
